// First, before anything reads localStorage: a view-as tab gets a store of its own (src/lib/viewAs.ts).
import '@/lib/viewAsBoot'
import React from 'react'
import { hydrateRoot, createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { HelmetProvider } from "react-helmet-async"
import { TooltipProvider } from "@/components/ui/tooltip"
import { Toaster } from "@/components/ui/toaster"
import { Toaster as Sonner } from "@/components/ui/sonner"
import App from './App.tsx'
import './index.css'
import { preloadRouteFor, routeChunkFor } from '@/routes/marketingRoutes'
import { initSentry } from '@/lib/sentry'
import { installStuckWatchdog } from '@/lib/watchdog'
import { installTranslateGuard } from '@/lib/translateGuard'
import { installAccountSwitchGuard } from '@/lib/accountSwitch'
import { supabase } from '@/integrations/supabase/client'
import { captureAppsumoLicenseFromUrl } from '@/lib/appsumoLicense'
import { takeHandoffArrival } from '@/lib/attribution/keeper'

const rootElement = document.getElementById('root')!;
initSentry();
installStuckWatchdog();
// A page the browser translated never drops to Something went wrong (lib/translateGuard.ts, Sentry 30 Oct 26).
installTranslateGuard();
// Nothing from one account shows under another: a switch drops the last account's tab copies (lib/accountSwitch.ts).
installAccountSwitchGuard(supabase.auth);
// Capture an AppSumo license from the landing URL BEFORE React renders or any
// auth redirect strips the query param. Stored in localStorage; while stashed,
// AuthProvider's safety net snaps navigation to /appsumo/welcome, the page
// that owns the whole claim flow. This must live HERE: index.html loads
// entry-client.tsx, main.tsx is not the production entry.
captureAppsumoLicenseFromUrl(window.location.search);
// An in-app browser's hand-off (?fly_src=, src/lib/attribution/handoff.ts):
// its source taken in and off the address before React renders, so no tag
// and no page view ever reads it (ATTR-1).
takeHandoffArrival();
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Keep data in cache for 5 minutes
      staleTime: 5 * 60 * 1000,
      // Keep unused data in cache for 10 minutes
      gcTime: 10 * 60 * 1000,
      // Don't refetch on window focus by default (prevents unwanted refreshes)
      refetchOnWindowFocus: false,
      // Retry failed requests once
      retry: 1,
    },
  },
})

// Check if this is a server-rendered page
// SSR pages can have window.__SSR_DATA__ (for microsites with data fetching)
// OR just have server-rendered content (for public routes without data)
const hasSSRData = typeof window !== 'undefined' && '__SSR_DATA__' in window;
const hasServerContent = rootElement.innerHTML.trim().length > 0;

// Force CSR for localhost (local development)
// Only use SSR for production/Vercel deployments
const isLocalhost = window.location.hostname === 'localhost' ||
                    window.location.hostname === '127.0.0.1' ||
                    window.location.hostname.includes('192.168.');

const isSSRPage = !isLocalhost && hasServerContent; // Only use SSR in production

const MAIN_DOMAINS = ['fly-social.com', 'www.fly-social.com'];
const isVercelPreview = window.location.hostname.endsWith('.vercel.app');
const isAgencyDomain = !isLocalhost && !isVercelPreview && !MAIN_DOMAINS.includes(window.location.hostname);

// Microsites always have __SSR_DATA__ injected by the server.
// Agency dashboards use CSR (no SSR data). So we can distinguish:
const isAgencyMicrosite = isAgencyDomain && hasSSRData;
const isAgencyDashboard = isAgencyDomain && !hasSSRData;

console.log('[entry-client] Initializing...', {
  hasSSRData,
  hasServerContent,
  isLocalhost,
  isVercelPreview,
  isSSRPage,
  hostname: window.location.hostname,
  isAgencyDomain,
  isAgencyMicrosite,
  isAgencyDashboard,
  rootContentLength: rootElement.innerHTML.length
});

if (isAgencyMicrosite) {
  console.log('[entry-client] Agency microsite detected:', window.location.hostname);
} else if (isAgencyDashboard) {
  console.log('[entry-client] Agency dashboard detected:', window.location.hostname);
} else if (isVercelPreview) {
  console.log('[entry-client] Vercel preview deployment');
} else {
  console.log('[entry-client] Main domain (fly-social.com)');
}

const AppWithProviders = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

if (isSSRPage) {
  // SSR: Hydrate server-rendered HTML
  // Used for:
  // 1. Microsite subdomains (landing pages, store locators) - with SSR data
  // 2. Public routes on main domain (marketing, tools, blogs) - without SSR data
  console.log('[entry-client] Using SSR hydration', hasSSRData ? '(with SSR data)' : '(without SSR data)');

  // If this URL's page lives in its own chunk, load the chunk BEFORE
  // hydrating. The server now renders these pages for real (see
  // src/lib/lazyRoute.tsx), so the HTML in the document is the finished page.
  // Starting hydrateRoot before the chunk has arrived would make the route's
  // Suspense boundary fall back, and React treats a boundary that suspends
  // during hydration as a failed hydration: it throws away the server DOM,
  // re-renders that subtree on the client and reports a recoverable error.
  // The visible result is a flash of empty page on a page that had already
  // painted. Awaiting the chunk first means server and client render the
  // identical tree.
  //
  // This costs no extra round trip. React would have started the same import
  // during hydration anyway; we are only choosing to paint the finished HTML
  // while it lands instead of replacing it with a fallback. preloadRouteFor
  // never rejects and gives up after its timeout, so a chunk that fails to
  // load still hydrates (with the old mismatch) rather than leaving a page
  // that never becomes interactive.
  //
  // Gated on !hasSSRData so this only ever applies to the main domain.
  // Customer microsites are served on their own subdomain with __SSR_DATA__
  // injected, and they use paths of their own like /blogs and /contact-us —
  // without this check a microsite visitor would download fly-social.com's
  // blog chunk, which they never render, and wait for it before hydrating.
  const hydrate = () => hydrateRoot(rootElement, <AppWithProviders />);

  if (!hasSSRData && routeChunkFor(window.location.pathname)) {
    preloadRouteFor(window.location.pathname).then(hydrate);
  } else {
    hydrate();
  }
} else {
  // CSR: Client-side render from scratch
  // Used for authenticated routes (/fly-app/*) where server returns empty HTML shell
  console.log('[entry-client] Using CSR (client-side rendering)');
  createRoot(rootElement).render(
    <AppWithProviders />
  );
}
